import React, {useEffect} from "react";
import {RouteComponentProps, Route, useHistory, useParams} from "react-router-dom";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {Text, View} from "@unibuddy/patron";

import {CHAT_SERVICE_FLOW_ENABLED} from "../../../../shared/constants";
import {ChatProvider} from "../providers/ChatProvider";
import {useChatIdResolver} from "../hooks/useChatIdResolver";

import LoadingChat from "../components/LoadingChat/LoadingChat";
import {DownloadUnibuddyAppBanner} from "../../DownloadUnibuddyApp/DownloadUnibuddyAppBanner";
import {pageNames} from "../../../../shared/AnalyticsNew/constants/productSpecification";
import {RegisterPage2Wrapper} from "../../RegisterNew/components/RegisterPage2Wrapper";
import {ChatLegacy} from "../containers/Chat/ChatLegacy";
import {isExternalBuddycard} from "../../../../shared/Utils/WhereAmI";
import {AppContainer} from "../../../shared/components/Containers/AppContainer";
import {Chat} from "../containers/Chat/Chat";
import useErrorReporting from "../../../../shared/General/components/ErrorReporting/useErrorReporting";

type ChatUrlNormaliserProps = RouteComponentProps<{id: string}>;

export const ChatUrlNormaliser: React.FC<ChatUrlNormaliserProps> = ({match}) => {
    const {id: urlId} = useParams<{id: string}>();
    const history = useHistory();
    const isChatServiceEnabled = useBooleanFeatureFlag(
        CHAT_SERVICE_FLOW_ENABLED.key,
        CHAT_SERVICE_FLOW_ENABLED.value,
    );
    const {conversationId, chatGroupId, loading, error} = useChatIdResolver(urlId);
    const isExternalBuddycardPath = isExternalBuddycard(window.location.pathname);
    const {reportError} = useErrorReporting();

    useEffect(() => {
        if (!loading && !error && urlId) {
            let targetIdForUrl = "";
            let isCurrentUrlNormalised = false;

            if (isChatServiceEnabled) {
                targetIdForUrl = conversationId;
                isCurrentUrlNormalised = urlId === conversationId;
            } else {
                targetIdForUrl = chatGroupId;
                isCurrentUrlNormalised = urlId === chatGroupId;
            }

            if (!isCurrentUrlNormalised && targetIdForUrl) {
                const currentPath = history.location.pathname;
                const currentSearch = history.location.search;
                const newPath = currentPath.replace(urlId, targetIdForUrl) + currentSearch;
                history.replace(newPath);
            } else if (!targetIdForUrl && urlId) {
                reportError(
                    new Error(
                        `Invalid ID ${urlId}. Could not resolve a valid chat group or conversation`,
                    ),
                );
            }
        }
    }, [
        isChatServiceEnabled,
        urlId,
        conversationId,
        chatGroupId,
        loading,
        error,
        history,
        reportError,
    ]);

    if (loading) {
        if (isExternalBuddycardPath) {
            return (
                <View h="550px" w="100%">
                    <AppContainer minW="320px" width="832px">
                        <LoadingChat />
                    </AppContainer>
                </View>
            );
        }
        return (
            <>
                <DownloadUnibuddyAppBanner page={pageNames.chat} />
                <View h="550px">
                    <LoadingChat />
                </View>
            </>
        );
    }

    if (error) {
        return <Text>An error occurred. Please try again later.</Text>;
    }

    if (isExternalBuddycardPath) {
        return (
            <>
                {isChatServiceEnabled ? (
                    <Route
                        exact
                        path={`${match.path.replace(":id", ":conversationId")}`}
                        render={() => (
                            <RegisterPage2Wrapper>
                                <AppContainer>
                                    <ChatProvider>
                                        <Chat />
                                    </ChatProvider>
                                </AppContainer>
                            </RegisterPage2Wrapper>
                        )}
                    />
                ) : (
                    <Route
                        exact
                        path={`${match.path.replace(":id", ":chatGroupId")}`}
                        render={() => (
                            <RegisterPage2Wrapper>
                                <AppContainer>
                                    <ChatProvider>
                                        <ChatLegacy />
                                    </ChatProvider>
                                </AppContainer>
                            </RegisterPage2Wrapper>
                        )}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <DownloadUnibuddyAppBanner page={pageNames.chat} />
            {isChatServiceEnabled ? (
                <Route
                    exact
                    path={`${match.path.replace(":id", ":conversationId")}`}
                    render={() => (
                        <ChatProvider>
                            <Chat />
                        </ChatProvider>
                    )}
                />
            ) : (
                <Route
                    exact
                    path={`${match.path.replace(":id", ":chatGroupId")}`}
                    render={() => (
                        <ChatProvider>
                            <RegisterPage2Wrapper>
                                <ChatLegacy />
                            </RegisterPage2Wrapper>
                        </ChatProvider>
                    )}
                />
            )}
        </>
    );
};
